import { Button } from '@/components/ui/button'
import { Play, ArrowRight } from 'lucide-react'
import { Link } from 'react-router'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import Header from '@/components/Header'
import PartnerModal from './components/PartnerModal'
import ContactForm from './components/ContactForm'
import heroImg from '@/assets/Landing/heroimg.svg'
import aboutImg from '@/assets/Landing/aboutimg.svg'
import learningHubImg from '@/assets/Landing/learninghub.svg'
import redFlagsImg from '@/assets/Landing/redflagsimg.svg'
import irisWhiteLogo from '@/assets/iriswhite.svg'

const IRIS_VIDEOS = {
  intro: 'https://www.youtube.com/watch?v=bWL4pmerg5Q',
  bambamOdogwu: 'https://www.instagram.com/reel/DSc68O3DWUS/',
  maryIpv: 'https://www.instagram.com/reel/DUVvPBYiIhW/',
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <main id="hero" className="grid lg:grid-cols-2 gap-0 lg:gap-0">
        {/* Left Content */}
        <div className="flex flex-col justify-center space-y-6 lg:space-y-8 px-6 lg:px-16 xl:px-[174px] py-12 lg:py-16">
          <div className="space-y-1 lg:space-y-2">
            <h2 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl 2xl:text-[128px] font-extrabold leading-[1.12] text-left">
              <span className="text-[#1c1c1f]">Identify</span>
              <span className="text-[#f60]">.</span>
            </h2>
            <h2 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl 2xl:text-[128px] font-extrabold leading-[1.12] text-left">
              <span className="text-[#1c1c1f]">Learn</span>
              <span className="text-[#f60]">.</span>
            </h2>
            <h2 className="text-4xl sm:text-5xl lg:text-7xl xl:text-8xl 2xl:text-[128px] font-extrabold leading-[1.12] text-left">
              <span className="text-[#1c1c1f]">Transform</span>
              <span className="text-[#f60]">.</span>
            </h2>
          </div>

         <div className='flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
             <p className="text-[#1d1d1d] text-[14px] sm:text-[16px] max-w-[370px] leading-[22px] sm:leading-[24px] text-left">
            Uncover the patterns shaping your relationships, grow in empathy, and receive a certificate that marks your journey toward healthier love.
          </p>

          {/* Stats Badge */}
          <div className="inline-flex items-center gap-3 bg-[#f1f1f1] rounded-[20px] px-4 sm:px-5 py-3 w-fit shrink-0">
            <span className="text-[#f60] font-bold text-[18px] leading-[28px]">125K+</span>
            <span className="text-[#383838] text-[16px] leading-[24px]">People joined</span>
            <div className="flex -space-x-2">
              <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-white">
                <img
                  src="https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=100&h=100&fit=crop&crop=faces"
                  alt="Community member"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-white">
                <img
                  src="https://images.unsplash.com/photo-1539701938214-0d9736e1c16b?w=100&h=100&fit=crop&crop=faces"
                  alt="Community member"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-7 h-7 rounded-full overflow-hidden border-2 border-white">
                <img
                  src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop&crop=faces"
                  alt="Community member"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="w-7 h-7 rounded-full bg-gray-900 border-2 border-white flex items-center justify-center">
                <span className="text-white text-xs font-bold">+</span>
              </div>
            </div>
          </div>
         </div>

          {/* CTA Buttons */}
          <div className="flex flex-wrap items-center gap-[20px]">
            <Button
              className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-[40px] py-[24px] text-[18px] lg:text-[20px] font-bold transition-all h-auto"
              asChild
            >
              <Link to="/assessment">Get Started</Link>
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="rounded-full w-12 h-12 border border-orange-500 text-orange-500 hover:bg-orange-50"
              asChild
            >
              <a
                href={IRIS_VIDEOS.intro}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Watch IRIS intro video"
              >
                <Play className="w-4 h-4 fill-orange-500" />
              </a>
            </Button>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative flex justify-end">
          <div className="relative">
            <img 
              src={heroImg} 
              alt="Happy couple embracing" 
              className="h-auto object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#FF6600]/70 to-transparent mix-blend-multiply pointer-events-none"></div>
          </div>
        </div>
      </main>

      {/* Partner Logos */}
      {/* <section className="px-6 lg:px-16 xl:px-[174px] py-12 border-t border-gray-100">
        <div className="flex flex-wrap items-center justify-center lg:justify-between gap-8 lg:gap-12 opacity-40">
          <div className="font-serif italic text-2xl lg:text-3xl text-gray-400">Chicly</div>
          <div className="font-serif text-2xl lg:text-3xl text-gray-400">StyleAura</div>
          <div className="font-serif text-2xl lg:text-3xl text-gray-400">StyleAura</div>
          <div className="font-serif italic text-2xl lg:text-3xl text-gray-400">Nexura</div>
          <div className="font-serif text-2xl lg:text-3xl text-gray-400">StyleAura</div>
          <div className="font-serif italic text-2xl lg:text-3xl text-gray-400">Nexura</div>
          <div className="font-serif italic text-2xl lg:text-3xl text-gray-400">Nexura</div>
          <div className="font-serif text-2xl lg:text-3xl text-gray-400">StyleAura</div>
        </div>
      </section> */}

      {/* About Section */}
      <section id="about" className="bg-gray-50 px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:space-x-20 space-y-8 lg:space-y-0">
            <div className="flex w-full lg:w-[20%]">
              <span className="px-[11px] py-[6px] border border-[#f60] rounded-[48px] text-[#1c1c1f] text-[16px]">
                About us
              </span>
            </div>
          {/* Left Content */}
          <div className="space-y-8 w-full">
            <p className="text-[#1d1d1d] text-base lg:text-xl xl:text-[36px] leading-[1.5] text-left">
              IRIS helps individuals and couples build healthier relationships through self-awareness and guided learning. Our faith-informed assessments and modules promote empathy, respect, and emotional growth, supporting counsellors and faith communities in nurturing love built on understanding and accountability.
            </p>

           <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 lg:gap-12'>
             <div className="space-y-6 text-start flex-1">
              <h3 className="text-2xl lg:text-3xl xl:text-[48px] font-extrabold text-[#1c1c1f] leading-[1.2] text-start">
                Build Healthier Relationships Through Self-Awareness
              </h3>

              {/* <Button
                className="bg-[#f60] hover:bg-[#e55a00] text-[#151515] rounded-[75px] px-[20px] py-[15px] text-[16px] font-semibold transition-all group"
                asChild
              >
                <a href={IRIS_VIDEOS.intro} target="_blank" rel="noopener noreferrer">
                  How it works
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </Button> */}
            </div>

              {/* Right Image */}
          <div className="relative flex justify-center lg:justify-end w-full lg:w-auto flex-shrink-0">
            <div className="relative w-full max-w-xs lg:max-w-md">
              <img 
                src={aboutImg} 
                alt="Couple embracing" 
                className="w-full h-auto rounded-[30px] object-cover shadow-lg"
              />
            </div>
          </div>
           </div>
          </div>

        
        </div>
      </section>

      {/* How it Works Section */}
      <section id="how-it-works" className="bg-white px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 xl:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8 text-start">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl 2xl:text-[96px] font-extrabold text-[#1c1c1f] leading-[1.2]">
              <span>How it </span>
              <br />
              <span>Works</span>
              <span className="text-[#f60]">.</span>
            </h2>

            <p className="text-[#1d1d1d] text-[16px] leading-[1.5] max-w-[541px]">
              See how our guided assessments and learning modules empower individuals and couples to grow together in understanding and emotional maturity.
            </p>

            <Button
              className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-[20px] py-[15px] text-[16px] font-semibold transition-all group"
              asChild
            >
              <a href={IRIS_VIDEOS.bambamOdogwu} target="_blank" rel="noopener noreferrer">
                Discover More
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>

          {/* Right Cards */}
          <div className="relative flex items-center justify-center hidden md:flex min-h-[400px] lg:min-h-full">
            <div className="relative w-full h-full flex items-center justify-center">
              <img 
                src={learningHubImg} 
                alt="Learning Hub with assessment cards" 
                className='w-full max-w-full h-auto object-contain'
              />
            </div>
          </div>
        </div>
      </section>

      {/* Faith Meets Psychology Section */}
      <section className="bg-gray-50 px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="space-y-12 lg:space-y-16">
          {/* Section Heading */}
          <h2 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl 2xl:text-[96px] font-extrabold text-[#1c1c1f] leading-[1.2] text-left max-w-[1188px]">
            Faith Meets Psychology for Stronger Relationships<span className="text-[#f60]">.</span>
          </h2>

          {/* Content Grid */}
          <div className="flex flex-col md:flex-row items-start justify-between gap-8 lg:gap-12 h-full">
            {/* Left - Video */}
            <div className="relative w-full rounded-[20px] md:rounded-[40px] overflow-hidden bg-[#151515] shadow-2xl hidden md:flex aspect-[16/9] lg:aspect-[2.5/1] items-center justify-center">
              <iframe
                src={`${IRIS_VIDEOS.maryIpv}embed`}
                title="Watch IRIS IPV awareness video"
                className="h-[180%] w-[min(360px,55%)] border-0"
                loading="lazy"
                allow="encrypted-media"
                allowFullScreen
              />
            </div>

            {/* Right - Info Card */}
            <div className="bg-[#e6e6e6] rounded-[20px] md:rounded-[40px] p-6 md:p-[38px] space-y-6 text-start flex flex-col w-full md:min-w-[280px] lg:min-w-[411px]">
              <span className="inline-block text-[#1c1c1f] text-[20px] capitalize leading-[1.32] text-center">
                visual
              </span>

              <h3 className="text-xl lg:text-[24px] font-bold text-[#1c1c1f] leading-[32px]">
                Equip Your Counselling Sessions with Meaningful Assessments
              </h3>

              <PartnerModal
                trigger={
                  <Button
                    className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-[20px] py-[15px] text-[20px] font-semibold capitalize transition-all group w-full"
                  >
                    Let's Collaborate
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                }
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonies Section */}
      <section id="testimony" className="bg-white px-6 lg:px-16 xl:px-[174px] py-16 lg:py-20">
        <div className="space-y-8 lg:space-y-12">
          {/* Section Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl 2xl:text-[96px] font-extrabold text-[#1c1c1f] text-center leading-[1.2]">
            Testimonies
          </h2>

          {/* Testimonial Cards Grid */}
          <div className="grid md:grid-cols-3 gap-4 lg:gap-6 mx-auto">
            {[
              {
                name: 'Tolu & Kemi Adebayo',
                role: 'Engaged Couple',
                quote:
                  'Before using IRIS, we thought love was enough. The assessment opened our eyes to how we handled conflict and communication. It helped us address issues we didn’t even realize could become serious problems later. Today, we’re more intentional, more patient, and better prepared for marriage.',
              },
              {
                name: 'Chinedu Okafor',
                role: 'Married Professional',
                quote:
                  'IRIS made me confront parts of myself I had always ignored. I used to justify my reactions, but the insights showed me patterns that could harm my relationship. The guidance helped me change how I communicate and manage my emotions. It’s been a turning point for me as a husband.',
              },
              {
                name: 'Pastor (Mrs.) Funke Adeyemi',
                role: 'Marriage Counsellor & Pastor',
                quote:
                  'IRIS has become a powerful tool in our counselling sessions. It helps couples see beyond emotions and understand their behavioral patterns clearly. We’ve seen real transformation in relationships because people are now more self-aware and willing to grow.',
              },
            ].map((testimony) => (
              <div
                key={testimony.name}
                className="bg-[#e6e6e6] rounded-[20px] md:rounded-[40px] overflow-hidden relative min-h-[360px] sm:min-h-[420px] md:h-[521px] p-6 sm:p-10 md:p-[55px] flex flex-col"
              >
                <div className="text-[40px] sm:text-[56px] md:text-[72px] font-bold text-[#1e1e1e] leading-none">
                  &ldquo;
                </div>
                <p className="text-[16px] sm:text-[18px] md:text-[20px] text-[#1e1e1e] leading-normal mt-4 sm:mt-8">
                  {testimony.quote}
                </p>
                <div className="mt-auto pt-6">
                  <h3 className="text-[20px] md:text-[24px] font-bold text-[#151515] leading-[32px]">
                    {testimony.name}
                  </h3>
                  <p className="text-[16px] md:text-[20px] text-[#4e4d4d] leading-[32px]">{testimony.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Red Flags CTA Section */}
      <section className="bg-white px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="bg-gradient-to-r from-gray-200 via-gray-200 to-[#151515] rounded-[40px] overflow-hidden">
          <div className="grid lg:grid-cols-2 items-center">
            {/* Left - Illustration */}
            <div className="relative bg-gray-200">
              <img
                src={redFlagsImg}
                alt="Spot the red flags illustration"
                className="w-full h-auto object-contain"
              />
            </div>

            {/* Right - Content */}
            <div className="bg-[#151515] h-full flex flex-col items-center justify-center space-y-8 lg:space-y-10 text-white p-8 lg:p-0">
              <h2 className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl 2xl:text-[72px] font-bold leading-tight text-center lg:text-start">
                Spot the <span className="text-[#f60]">RED FLAGS</span>,<br />
                before you say <span className="text-[#f60]">I DO</span>.
              </h2>

              <div className="flex items-center gap-4 flex-row justify-center lg:justify-start">
                {/* Avatar Group */}
                <div className="hidden lg:flex -space-x-3">
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 border-[#151515]">
                    <img
                      src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop&crop=faces"
                      alt="User"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 border-[#151515]">
                    <img
                      src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=faces"
                      alt="User"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full overflow-hidden border-2 border-[#151515]">
                    <img
                      src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=faces"
                      alt="User"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <span className="hidden lg:inline text-white/80 text-sm lg:text-base">+</span>

                {/* CTA Button */}
                <Button
                  className="bg-[#f60] hover:bg-[#e55a00] text-[#1c1c1f] rounded-[75px] px-[20px] py-[15px] text-[20px] font-semibold transition-all group"
                  asChild
                >
                  <Link to="/assessment">
                    Get Started
                    <ArrowRight className="ml-2 w-4 h-4 lg:w-5 lg:h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section id="faqs" className="bg-gray-50 px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="mx-auto space-y-12">
          {/* Section Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl 2xl:text-[96px] font-extrabold text-[#1c1c1f] text-center leading-[1.2]">
            FAQs
          </h2>

          {/* Accordion */}
          <div className="bg-white rounded-[32px] p-6 lg:p-[56px]">
            <Accordion type="single" defaultValue="item-1" className="w-full">
              {[
                {
                  value: 'item-1',
                  question: 'What is IRIS?',
                  answer:
                    'IRIS is a faith informed relationship intelligence platform from Citizens Gavel. It helps individuals and couples build healthier relationships through guided self assessments, learning modules, and counsellor support, so love is nurtured with self awareness, empathy, and accountability.',
                },
                {
                  value: 'item-2',
                  question: 'Who is IRIS designed for?',
                  answer:
                    'IRIS is built for singles exploring their readiness for relationships, engaged and married couples who want to grow together, and counsellors, pastors, and faith communities who walk with people through their relationship journey.',
                },
                {
                  value: 'item-3',
                  question: 'How does the assessment work?',
                  answer:
                    'You answer a set of guided questions covering communication, conflict, values, emotional regulation, and family background. IRIS then generates a personalised report that highlights your strengths, patterns to watch, and specific areas where growth will make the biggest difference.',
                },
                {
                  value: 'item-4',
                  question: 'Is IRIS only for people about to get married?',
                  answer:
                    'No. While engaged couples use IRIS to prepare for marriage, it is just as useful for singles working on self awareness, married couples strengthening their bond, and anyone recovering from an unhealthy relationship pattern.',
                },
                {
                  value: 'item-5',
                  question: 'Is my information private and secure?',
                  answer:
                    'Yes. Your responses and reports are confidential. Only you and any counsellor you explicitly share your results with can see them. We follow strict data protection practices and never sell or share personal information.',
                },
                {
                  value: 'item-6',
                  question: 'Do I receive a certificate after completing IRIS?',
                  answer:
                    'Yes. Once you complete the assessment and the recommended learning modules, you receive a certificate that marks your journey toward healthier, more intentional love. Many churches and counsellors accept this as part of pre marital preparation.',
                },
                {
                  value: 'item-7',
                  question: 'Can counsellors, churches, and organizations partner with IRIS?',
                  answer:
                    'Absolutely. IRIS equips counsellors and faith communities with meaningful assessments and tools that make their sessions more effective. Use the Let\u2019s Collaborate button on this page to share your details and our team will reach out.',
                },
                {
                  value: 'item-8',
                  question: 'How much does IRIS cost?',
                  answer:
                    'Getting started with IRIS is free. Some advanced modules, counsellor integrations, and partnership plans for organizations are available as paid options. Reach out through the contact form to learn what fits your situation best.',
                },
              ].map((faq) => (
                <AccordionItem key={faq.value} value={faq.value}>
                  <AccordionTrigger className="text-lg lg:text-[20px] font-bold text-[#181d27] leading-[32px] text-start">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm lg:text-[16px] text-[#747986] leading-[1.7] text-start">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="bg-white px-6 lg:px-16 xl:px-[174px] py-16 lg:py-24">
        <div className="max-w-3xl mx-auto space-y-12">
          {/* Section Heading */}
          <h2 className="text-3xl sm:text-4xl lg:text-6xl xl:text-7xl 2xl:text-[96px] font-extrabold text-[#1c1c1f] text-center leading-[1.2]">
            Contact Us
          </h2>

          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#151515] text-white">
        {/* Main Footer Content */}
        <div className="px-6 lg:px-16 py-12 lg:py-20 border-b border-gray-800">
          <div className="mx-auto flex flex-col lg:flex-row gap-12 lg:gap-[154px] items-start">
            {/* Left Section - Branding and Description */}
            <div className="flex flex-col gap-[40px] text-start w-full lg:w-[618px] shrink-0">
              <div className="flex items-center gap-3">
                <img src={irisWhiteLogo} alt="IRIS Logo" className="h-8 lg:h-10" />
              
              </div>
              <p className="text-gray-400 text-[16px] leading-[24px] max-w-[531px]">
                Uncover the patterns shaping your relationships, grow in empathy, and receive a certificate that marks your journey toward healthier love.
              </p>
            </div>

            {/* Right Section - Nav + Contact */}
            <div className="flex flex-col gap-[49px] items-center w-full">
              {/* Navigation Links with dividers */}
              <nav className="flex flex-wrap items-center justify-center">
                <Link to="/" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  Home
                </Link>
                <div className="w-[1px] h-[32px] bg-gray-600" />
                <Link to="/about" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  About
                </Link>
                <div className="w-[1px] h-[32px] bg-gray-600" />
                <Link to="/how-it-works" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  How It Works
                </Link>
                <div className="w-[1px] h-[32px] bg-gray-600" />
                <Link to="/testimony" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  Testimony
                </Link>
                <div className="w-[1px] h-[32px] bg-gray-600" />
                <Link to="/faqs" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  FAQs
                </Link>
                <div className="w-[1px] h-[32px] bg-gray-600" />
                <Link to="/contact-us" className="text-white hover:text-orange-500 transition-colors font-bold text-[16px] leading-[24px] px-[26px] py-[9px]">
                  Contact Us
                </Link>
              </nav>

              {/* Contact Information */}
              <div className="flex flex-col gap-[60px] items-start w-full text-[20px] leading-[32px]">
                <div className="flex flex-col gap-[16px] items-start">
                  <p className="text-[#a1a1a1] text-[20px] leading-[32px]">Contact Us</p>
                  <div className="flex flex-col font-bold text-white text-start">
                    <p>+234 8102842542</p>
                    <p>hello@gavel.ng</p>
                  </div>
                </div>

                <div className="flex flex-col gap-[16px] items-start">
                  <p className="text-[#a1a1a1] text-[20px] leading-[32px]">Location</p>
                  <p className="font-bold text-white text-start">
                    5 Atiba Street, Soun Ajagungbade Estate,<br />
                    Aare, Bodija, Ibadan, Oyo State
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="px-6 lg:px-16 py-6" style={{ background: 'linear-gradient(90deg, rgba(111,111,111,0.2) 0%, rgba(111,111,111,0.2) 100%), linear-gradient(90deg, #1c1c1f 0%, #1c1c1f 100%)' }}>
          <div className="flex items-center justify-center text-center">
            <p className="text-white font-bold text-[20px] leading-[32px]">
              Powered By <span className="text-orange-500 font-bold">GAVEL</span>.
            </p>
          </div>
        </div>
      </footer>
  
    </div>
  )
}
